/**
 * Attendance history export (PDF / Excel) for the "View history" modal.
 *
 * Both exports share one row builder so the two files always carry the same
 * columns: date, day, check-in/out, hours, overtime, tardiness, status,
 * lateness tier, overtime approval, punch locations, manual-edit audit and
 * notes — every field the Attendance record stores.
 */

const pad = (n) => String(n).padStart(2, "0");

/** Minutes → "3h 05m" (or "-" when zero/empty). */
export const formatMinutes = (mins, language) => {
  const m = Number(mins) || 0;
  if (!m) return "-";
  const h = Math.floor(Math.abs(m) / 60);
  const r = Math.abs(m) % 60;
  return language === "ar" ? `${h}س ${pad(r)}د` : `${h}h ${pad(r)}m`;
};

const localeOf = (language) => (language === "ar" ? "ar-EG" : "en-US");

const fmtDate = (value, language) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString(localeOf(language), { year: "numeric", month: "2-digit", day: "2-digit" });
};

const fmtDay = (value, language) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString(localeOf(language), { weekday: "long" });
};

const fmtTime = (value, language) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleTimeString(localeOf(language), { hour12: false, hour: "2-digit", minute: "2-digit" });
};

const fmtApproval = (status, language) => {
  if (!status) return "-";
  const map = {
    en: { pending: "Pending", approved: "Approved", rejected: "Rejected" },
    ar: { pending: "قيد الانتظار", approved: "معتمد", rejected: "مرفوض" },
  };
  return map[language === "ar" ? "ar" : "en"][String(status).toLowerCase()] || status;
};

/** Records that actually represent a day (the API may prepend a totals-only object). */
export const dataRecords = (records) => (Array.isArray(records) ? records.filter((r) => r && r.date) : []);

/** Column definitions, in export order. `t` is the attendance translations object. */
export const historyColumns = (t) => [
  { key: "date", label: t.date, wch: 12 },
  { key: "day", label: t.day, wch: 12 },
  { key: "checkIn", label: t.checkIn, wch: 10 },
  { key: "checkOut", label: t.checkOut, wch: 10 },
  { key: "hoursWorked", label: t.hoursWorked, wch: 12 },
  { key: "overtime", label: t.overtime, wch: 11 },
  { key: "tardiness", label: t.tardiness, wch: 11 },
  { key: "status", label: t.status, wch: 12 },
  { key: "latenessTier", label: t.latenessTier, wch: 12 },
  { key: "overtimeApproval", label: t.overtimeApproval, wch: 16 },
  { key: "checkInLocation", label: t.checkInLocation, wch: 20 },
  { key: "checkOutLocation", label: t.checkOutLocation, wch: 20 },
  { key: "manuallyEdited", label: t.manuallyEdited, wch: 12 },
  { key: "editedByIn", label: t.editedByIn, wch: 18 },
  { key: "editedByOut", label: t.editedByOut, wch: 18 },
  { key: "notes", label: t.notes, wch: 30 },
];

/** One display row per attendance record (oldest first). */
export const buildHistoryRows = (records, t, language, getStatusText) =>
  dataRecords(records)
    .slice()
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .map((rec) => {
      const approval = [rec.overtimeApprovalStatus, rec.overtimeSecondApprovalStatus]
        .filter(Boolean)
        .map((s) => fmtApproval(s, language));
      return {
        date: fmtDate(rec.date, language),
        day: fmtDay(rec.date, language),
        checkIn: fmtTime(rec.checkIn, language),
        checkOut: fmtTime(rec.checkOut, language),
        hoursWorked: rec.workingHours != null && rec.workingHours !== "" ? Math.round(parseFloat(rec.workingHours) * 100) / 100 : "-",
        overtime: formatMinutes(rec.overTime, language),
        tardiness: formatMinutes(rec.underTime, language),
        status: getStatusText(rec.status, language),
        latenessTier: rec.latenessTier != null ? rec.latenessTier : "-",
        overtimeApproval: rec.overTime ? (approval.length ? approval.join(" / ") : "-") : "-",
        checkInLocation: rec.checkInLocationName || "-",
        checkOutLocation: rec.checkOutLocationName || "-",
        manuallyEdited: rec.manuallyEdited ? t.yes : t.no,
        editedByIn: rec.editedByIn || "-",
        editedByOut: rec.editedByOut || "-",
        notes: rec.notes || "-",
      };
    });

/** Sum of overtime / tardiness minutes and hours over the given records. */
export const historyTotals = (records) =>
  dataRecords(records).reduce(
    (acc, rec) => {
      acc.overtime += Number(rec.overTime) || 0;
      acc.undertime += Number(rec.underTime) || 0;
      acc.hours += parseFloat(rec.workingHours) || 0;
      acc.days += 1;
      return acc;
    },
    { overtime: 0, undertime: 0, hours: 0, days: 0 },
  );

const safeName = (s) => String(s || "").replace(/[^\p{L}\p{N}_-]+/gu, "_").slice(0, 40) || "employee";

const buildMeta = ({ t, language, employeeName, employeeId, from, to, records }) => {
  const totals = historyTotals(records);
  return [
    [t.employeeName, employeeName || "-"],
    [t.employeeID, employeeId || "-"],
    [t.historyPeriod, `${from || "-"} → ${to || "-"}`],
    [t.totalRecords, totals.days],
    [t.periodOvertimeTotal, formatMinutes(totals.overtime, language)],
    [t.periodTardinessTotal, formatMinutes(totals.undertime, language)],
    [t.hoursWorked, Math.round(totals.hours * 100) / 100],
    [t.generatedOn, new Date().toLocaleString(localeOf(language))],
  ];
};

/**
 * Excel export — one sheet: an info block, then the full detail table with a
 * styled header and a totals row.
 */
export const exportHistoryToExcel = async ({ records, t, language, employeeName, employeeId, from, to, getStatusText }) => {
  // CJS interop: webpack exposes the API on the namespace, Node on `default`.
  const xlsxMod = await import("xlsx-js-style");
  const XLSX = xlsxMod.utils ? xlsxMod : xlsxMod.default;
  const cols = historyColumns(t);
  const rows = buildHistoryRows(records, t, language, getStatusText);
  const meta = buildMeta({ t, language, employeeName, employeeId, from, to, records });
  const totals = historyTotals(records);

  const aoa = [
    [t.attendanceHistory],
    ...meta,
    [],
    cols.map((c) => c.label),
    ...rows.map((r) => cols.map((c) => r[c.key])),
    [
      language === "ar" ? "الإجمالي" : "Total",
      "", "", "",
      Math.round(totals.hours * 100) / 100,
      formatMinutes(totals.overtime, language),
      formatMinutes(totals.undertime, language),
    ],
  ];

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!cols"] = cols.map((c) => ({ wch: c.wch }));

  const headerRowIdx = 1 + meta.length + 1; // 0-based: title + meta + blank
  const border = { style: "thin", color: { rgb: "CBD5E1" } };
  const cellStyle = (extra = {}) => ({
    alignment: { horizontal: "center", vertical: "center", wrapText: true },
    border: { top: border, bottom: border, left: border, right: border },
    font: { name: "Arial", sz: 10 },
    ...extra,
  });

  // title + meta block
  ws["A1"].s = { font: { bold: true, sz: 14, name: "Arial" } };
  for (let r = 1; r <= meta.length; r++) {
    const k = XLSX.utils.encode_cell({ r, c: 0 });
    if (ws[k]) ws[k].s = { font: { bold: true, name: "Arial", sz: 10 } };
  }
  // header
  for (let c = 0; c < cols.length; c++) {
    const k = XLSX.utils.encode_cell({ r: headerRowIdx, c });
    if (ws[k]) ws[k].s = cellStyle({ font: { bold: true, color: { rgb: "FFFFFF" }, name: "Arial", sz: 10 }, fill: { fgColor: { rgb: "1E3A8A" } } });
  }
  // body + totals
  for (let r = headerRowIdx + 1; r < aoa.length; r++) {
    const isTotal = r === aoa.length - 1;
    for (let c = 0; c < cols.length; c++) {
      const k = XLSX.utils.encode_cell({ r, c });
      if (!ws[k]) ws[k] = { t: "s", v: "" };
      ws[k].s = cellStyle(isTotal ? { font: { bold: true, name: "Arial", sz: 10 }, fill: { fgColor: { rgb: "E2E8F0" } } } : {});
    }
  }
  ws["!freeze"] = { xSplit: 0, ySplit: headerRowIdx + 1 };

  const wb = XLSX.utils.book_new();
  if (language === "ar") wb.Workbook = { Views: [{ RTL: true }] };
  XLSX.utils.book_append_sheet(wb, ws, "Attendance");
  XLSX.writeFile(wb, `Attendance_${safeName(employeeId)}_${from}_${to}.xlsx`);
};

/**
 * PDF export — renders an HTML report (so Arabic shapes correctly) with
 * html2canvas and paginates the image into a landscape A4 jsPDF document.
 * Pages are cut on row boundaries (never through a row) and the table header
 * is repeated at the top of every continuation page.
 */
export const exportHistoryToPDF = async ({ records, t, language, isRTL, employeeName, employeeId, from, to, getStatusText }) => {
  const { default: jsPDF } = await import("jspdf");
  const { default: html2canvas } = await import("html2canvas");

  const cols = historyColumns(t);
  const rows = buildHistoryRows(records, t, language, getStatusText);
  const meta = buildMeta({ t, language, employeeName, employeeId, from, to, records });
  const esc = (v) => String(v ?? "").replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]));

  const wrapper = document.createElement("div");
  wrapper.style.cssText = "position:fixed;left:-10000px;top:0;width:1122px;background:#fff;color:#0f172a;padding:28px;box-sizing:border-box;";
  wrapper.dir = isRTL ? "rtl" : "ltr";
  wrapper.innerHTML = `
    <style>
      .ah-root{font-family:'Segoe UI',Tahoma,Arial,sans-serif;font-size:11px;}
      .ah-title{font-size:20px;font-weight:700;margin:0 0 10px;color:#1e3a8a;}
      .ah-meta{display:grid;grid-template-columns:repeat(4,1fr);gap:6px 18px;margin-bottom:14px;}
      .ah-meta div{background:#f1f5f9;border-radius:6px;padding:6px 10px;}
      .ah-meta b{display:block;font-size:10px;color:#475569;font-weight:600;}
      .ah-meta span{font-size:12px;font-weight:700;}
      table.ah{width:100%;border-collapse:collapse;}
      table.ah th{background:#1e3a8a;color:#fff;padding:7px 5px;font-size:10px;border:1px solid #cbd5e1;}
      table.ah td{padding:6px 5px;border:1px solid #cbd5e1;text-align:center;vertical-align:middle;}
      table.ah tr:nth-child(even) td{background:#f8fafc;}
      table.ah td.num{direction:ltr;font-variant-numeric:tabular-nums;}
    </style>
    <div class="ah-root">
      <h1 class="ah-title">${esc(t.attendanceHistory)}</h1>
      <div class="ah-meta">
        ${meta.map(([k, v]) => `<div><b>${esc(k)}</b><span>${esc(v)}</span></div>`).join("")}
      </div>
      <table class="ah">
        <thead><tr>${cols.map((c) => `<th>${esc(c.label)}</th>`).join("")}</tr></thead>
        <tbody>
          ${rows
            .map(
              (r) =>
                `<tr>${cols
                  .map((c) => `<td class="${["checkIn", "checkOut", "hoursWorked", "overtime", "tardiness"].includes(c.key) ? "num" : ""}">${esc(r[c.key])}</td>`)
                  .join("")}</tr>`,
            )
            .join("")}
        </tbody>
      </table>
    </div>`;
  document.body.appendChild(wrapper);

  try {
    // Measure the header and every row (DOM px, relative to the wrapper) so
    // pages can be cut between rows instead of through the middle of one.
    const W = 1122;
    const MARGIN = 28;
    const base = wrapper.getBoundingClientRect().top;
    const box = (el) => {
      const r = el.getBoundingClientRect();
      return { top: r.top - base, bottom: r.bottom - base };
    };
    const head = box(wrapper.querySelector("table.ah thead"));
    const rowBoxes = Array.from(wrapper.querySelectorAll("table.ah tbody tr")).map(box);

    const canvas = await html2canvas(wrapper, { scale: 1.5, backgroundColor: "#ffffff", logging: false, width: W, scrollX: 0, scrollY: 0 });
    const pdf = new jsPDF({ orientation: "landscape", unit: "px", format: "a4" });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const k = pageW / W; // DOM px → PDF units
    const s = canvas.width / W; // DOM px → canvas px
    const pageHDom = pageH / k;

    const crop = (y0, y1) => {
      const c = document.createElement("canvas");
      c.width = canvas.width;
      c.height = Math.max(1, Math.round((y1 - y0) * s));
      const ctx = c.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, c.width, c.height);
      ctx.drawImage(canvas, 0, Math.round(y0 * s), c.width, c.height, 0, 0, c.width, c.height);
      return c.toDataURL("image/jpeg", 0.93);
    };
    // Draw a DOM-px slice at `y` (DOM px), shrinking it if it can't fit the page.
    const draw = (y0, y1, y, maxH) => {
      const h = y1 - y0;
      const fit = Math.min(1, maxH / h);
      pdf.addImage(crop(y0, y1), "JPEG", 0, y * k, W * k * fit, h * k * fit);
      return h * fit;
    };

    // First page: title, meta, header and as many whole rows as fit.
    let i = 0;
    let end = head.bottom;
    while (i < rowBoxes.length && rowBoxes[i].bottom <= pageHDom - MARGIN) end = rowBoxes[i++].bottom;
    if (i === 0 && rowBoxes.length) end = rowBoxes[i++].bottom; // always make progress
    draw(0, i === rowBoxes.length ? Math.min(canvas.height / s, end + MARGIN) : end, 0, pageHDom);

    // Following pages: repeat the table header, then whole rows only.
    const headH = head.bottom - head.top;
    const avail = pageHDom - 2 * MARGIN - headH;
    while (i < rowBoxes.length) {
      const start = rowBoxes[i].top;
      let stop = rowBoxes[i++].bottom;
      while (i < rowBoxes.length && rowBoxes[i].bottom - start <= avail) stop = rowBoxes[i++].bottom;
      pdf.addPage();
      const drawnHead = draw(head.top, head.bottom, MARGIN, headH);
      draw(start, stop, MARGIN + drawnHead, pageHDom - 2 * MARGIN - drawnHead);
    }
    pdf.save(`Attendance_${safeName(employeeId)}_${from}_${to}.pdf`);
  } finally {
    document.body.removeChild(wrapper);
  }
};
